import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';

import { getAntibodies } from 'services/resources/commonResources';
import { useUnmountIgnore } from 'utilities/useUnmountIgnore';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { FETCH_ANTIBODIES_ERROR_MESSAGE } from 'constants/errorMessages';
import { IHC_ANTIBODIES_BASE_URL } from 'constants/urls';
import { AntibodyLinkList } from 'components/AntibodiesList/AntibodyLinkList';
import {
  FACETS,
  buildFacetGroups,
  getFacetUrl,
  isIndexableFacetGroup,
} from 'components/AntibodiesList/antibodyFacets';
import {
  ANTIBODIES_CATALOG_CANONICAL_URL,
  ANTIBODIES_INDEX_CANONICAL_URL,
  ANTIBODIES_INDEX_DESCRIPTION,
  ANTIBODIES_INDEX_TITLE,
  buildBreadcrumbJsonLd,
  buildCollectionJsonLd,
  toJsonLdString,
} from 'components/AntibodiesList/antibodySeo';
import 'components/AntibodiesList/styles.css';

const compareByName = (first, second) =>
  first.name.localeCompare(second.name, undefined, { sensitivity: 'base' });

// A crawlable index of the whole catalog, plus links into the facet listings. The
// interactive catalog on /ihc-antibodies/ renders its rows as buttons inside a virtualized
// grid, so it exposes no links for a crawler to follow; this page is plain anchors.
export const AntibodyIndexPage = () => {
  const unmountIgnore = useUnmountIgnore();
  const { showError } = useSnackbar();

  const [antibodies, setAntibodies] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    getAntibodies()
      .then((response) => {
        if (!unmountIgnore.current) {
          setAntibodies(response);
        }
      })
      .catch(() => showError(FETCH_ANTIBODIES_ERROR_MESSAGE))
      .finally(() => {
        if (!unmountIgnore.current) {
          setIsLoaded(true);
        }
      });
  }, [unmountIgnore]);

  const sortedAntibodies = useMemo(
    () => [...antibodies].sort(compareByName),
    [antibodies],
  );

  // Only link to facet pages that meet MIN_ANTIBODIES_PER_FACET; thinner pages are marked
  // noindex and omitted from the sitemap, so linking to them here would send crawlers into
  // noindex hubs.
  const facetGroups = useMemo(
    () =>
      isLoaded
        ? FACETS.map((facet) => ({
          facet,
          groups: buildFacetGroups(antibodies, facet).filter(
            isIndexableFacetGroup,
          ),
        })).filter(({ groups }) => groups.length > 0)
        : [],
    [antibodies, isLoaded],
  );

  const hasAntibodies = sortedAntibodies.length > 0;

  return (
    <Box component="main" sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
      {/* defer={false} applies the tags synchronously; helmet's default defers to
          requestAnimationFrame, which never runs in a hidden or non-compositing tab. */}
      <Helmet defer={false}>
        <title>{ANTIBODIES_INDEX_TITLE}</title>
        <meta name="description" content={ANTIBODIES_INDEX_DESCRIPTION} />
        <link rel="canonical" href={ANTIBODIES_INDEX_CANONICAL_URL} />
        <script type="application/ld+json">
          {toJsonLdString(
            buildCollectionJsonLd({
              title: ANTIBODIES_INDEX_TITLE,
              description: ANTIBODIES_INDEX_DESCRIPTION,
              url: ANTIBODIES_INDEX_CANONICAL_URL,
              antibodies: sortedAntibodies,
            }),
          )}
        </script>
        <script type="application/ld+json">
          {toJsonLdString(
            buildBreadcrumbJsonLd([
              {
                name: 'IHC Antibody Catalog',
                url: ANTIBODIES_CATALOG_CANONICAL_URL,
              },
              { name: 'All Antibodies', url: ANTIBODIES_INDEX_CANONICAL_URL },
            ]),
          )}
        </script>
      </Helmet>

      <Typography variant="h4" component="h1" gutterBottom>
        All IHC Antibodies
      </Typography>

      <Typography variant="body1" sx={{ maxWidth: 820, mb: 3 }}>
        {hasAntibodies
          ? `The complete list of ${sortedAntibodies.length} HistoWiz-validated IHC antibodies. Each entry links to its catalog page with the clone, host species, validated species, supported applications, positive control tissue and reference images.`
          : 'Loading the complete list of HistoWiz-validated IHC antibodies.'}{' '}
        <Link component={RouterLink} to={IHC_ANTIBODIES_BASE_URL}>
          Back to the catalog
        </Link>
        .
      </Typography>

      {facetGroups.map(({ facet, groups }) => (
        <Box key={facet.segment} mb={3}>
          <Typography variant="h5" component="h2" gutterBottom>
            {facet.listHeading}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {groups.map(({ slug, name, antibodies: members }) => (
              <Link
                key={slug}
                component={RouterLink}
                to={getFacetUrl(facet, slug)}
              >
                {name} ({members.length})
              </Link>
            ))}
          </Box>
        </Box>
      ))}

      <AntibodyLinkList antibodies={sortedAntibodies} />
    </Box>
  );
};
